import React from 'react'
import {
    Box,
    Button,
    Checkbox,
    Grid,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import StarsIcon from '@material-ui/icons/Stars'
import SendIcon from '@material-ui/icons/Send'
import LanguageIcon from '@material-ui/icons/Language'
import SearchIcon from '@material-ui/icons/Search'
import BarChartIcon from '@material-ui/icons/BarChart'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import SettingsIcon from '@material-ui/icons/Settings'
import VisibilityIcon from '@material-ui/icons/Visibility'
import PeopleIcon from '@material-ui/icons/People'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'

import { WebsiteSection } from '../types'
import { FLOOR_PLANS, WEBSITE_DOMAIN } from '../websiteData'
import { useWebsiteStyles } from '../useWebsiteStyles'
import { WebsiteStat } from './WebsiteStat'

const useStyles = makeStyles((theme: Theme) => ({
    aiBar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5),
    },
    previewChrome: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1, 1.5),
    },
    trafficDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
    },
    urlBar: {
        flex: 1,
        marginLeft: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 4,
        padding: theme.spacing(0.5, 1),
        fontFamily: 'monospace',
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
    },
    hero: {
        background: 'linear-gradient(to bottom right, #0F1B2D, #1a2d4d)',
        padding: theme.spacing(6),
        color: '#fff',
    },
    heroEyebrow: {
        fontSize: '0.625rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: theme.palette.primary.main,
    },
    heroSubtitle: {
        fontSize: '0.875rem',
        color: 'rgba(255,255,255,0.7)',
        marginTop: theme.spacing(1),
    },
    floorPlanCard: {
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5),
    },
    floorPlanImage: {
        width: '100%',
        aspectRatio: '16/9',
        borderRadius: 4,
        objectFit: 'cover',
        display: 'block',
        backgroundColor: theme.palette.grey[200],
    },
    quickActionBtn: {
        justifyContent: 'flex-start',
        textTransform: 'none',
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: theme.spacing(1, 1.25),
        border: `1px solid ${theme.palette.divider}`,
        width: '100%',
        marginBottom: theme.spacing(0.75),
    },
    sectionRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
    },
}))

const QUICK_ACTIONS = [
    { icon: LanguageIcon, label: 'Custom domain' },
    { icon: SearchIcon, label: 'SEO settings' },
    { icon: BarChartIcon, label: 'Analytics' },
    { icon: FileCopyIcon, label: 'Restyle from URL' },
    { icon: SettingsIcon, label: 'Advanced' },
]

interface SiteEditorProps {
    aiEdit: string
    setAiEdit: (value: string) => void
    sections: WebsiteSection[]
    setSections: (sections: WebsiteSection[]) => void
}

export const SiteEditor: React.FC<SiteEditorProps> = ({
    aiEdit,
    setAiEdit,
    sections,
    setSections,
}) => {
    const classes = useStyles()
    const sharedClasses = useWebsiteStyles()

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
                <Box className={`${sharedClasses.card} ${classes.aiBar}`}>
                    <StarsIcon fontSize="small" color="primary" />
                    <TextField
                        fullWidth
                        variant="standard"
                        value={aiEdit}
                        onChange={(e) => setAiEdit(e.target.value)}
                        placeholder="Tell AI what to change: 'Make the hero more vibrant' or 'Add a virtual tour button'…"
                        InputProps={{
                            disableUnderline: true,
                            style: { fontSize: '0.875rem' },
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<SendIcon style={{ fontSize: 12 }} />}
                        style={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            flexShrink: 0,
                        }}
                    >
                        Apply
                    </Button>
                </Box>

                <Box
                    className={sharedClasses.card}
                    mt={2}
                    style={{ overflow: 'hidden', padding: 0 }}
                >
                    <Box className={classes.previewChrome}>
                        <span
                            className={classes.trafficDot}
                            style={{ backgroundColor: '#f87171' }}
                        />
                        <span
                            className={classes.trafficDot}
                            style={{ backgroundColor: '#facc15' }}
                        />
                        <span
                            className={classes.trafficDot}
                            style={{ backgroundColor: '#4ade80' }}
                        />
                        <Box className={classes.urlBar}>{WEBSITE_DOMAIN}</Box>
                    </Box>
                    <Box className={classes.hero}>
                        <Typography className={classes.heroEyebrow}>
                            University Village
                        </Typography>
                        <Typography
                            variant="h4"
                            style={{ fontWeight: 700, marginTop: 8 }}
                        >
                            Your home base near UVA.
                        </Typography>
                        <Typography className={classes.heroSubtitle}>
                            Modern suites, full amenities, walking distance to
                            the Rotunda.
                        </Typography>
                        <Box mt={2} display="flex" style={{ gap: 8 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                style={{ fontWeight: 600, fontSize: '0.75rem' }}
                            >
                                Apply now
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                style={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: '#fff',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                }}
                            >
                                Book a tour
                            </Button>
                        </Box>
                    </Box>
                    <Grid container spacing={2} style={{ padding: 16 }}>
                        {FLOOR_PLANS.map((plan) => (
                            <Grid item xs={4} key={plan.id}>
                                <Box className={classes.floorPlanCard}>
                                    <img
                                        src={plan.image}
                                        alt={plan.name}
                                        className={classes.floorPlanImage}
                                    />
                                    <Typography
                                        variant="caption"
                                        style={{
                                            fontWeight: 600,
                                            display: 'block',
                                            marginTop: 8,
                                        }}
                                    >
                                        {plan.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ fontSize: '0.625rem' }}
                                    >
                                        From ${plan.price.toLocaleString()}/mo
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
                <Box className={sharedClasses.card} p={2} mb={2}>
                    <Typography className={sharedClasses.sectionLabel}>
                        Sections
                    </Typography>
                    <Box mt={1.5}>
                        {sections.map((s) => (
                            <Box key={s.id} className={classes.sectionRow}>
                                <Typography variant="body2">
                                    {s.label}
                                </Typography>
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    checked={s.on}
                                    onChange={() =>
                                        setSections(
                                            sections.map((x) =>
                                                x.id === s.id
                                                    ? { ...x, on: !x.on }
                                                    : x,
                                            ),
                                        )
                                    }
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box className={sharedClasses.card} p={2} mb={2}>
                    <Typography className={sharedClasses.sectionLabel}>
                        Quick actions
                    </Typography>
                    <Box mt={1.5}>
                        {QUICK_ACTIONS.map((a) => (
                            <Button
                                key={a.label}
                                className={classes.quickActionBtn}
                                startIcon={
                                    <a.icon
                                        fontSize="small"
                                        color="action"
                                        style={{ fontSize: 14 }}
                                    />
                                }
                            >
                                {a.label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                <Box className={sharedClasses.card} p={2}>
                    <Typography className={sharedClasses.sectionLabel}>
                        Last 30 days
                    </Typography>
                    <Grid container spacing={1} style={{ marginTop: 12 }}>
                        <Grid item xs={4}>
                            <WebsiteStat
                                icon={VisibilityIcon}
                                label="Views"
                                value="8.4k"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <WebsiteStat
                                icon={PeopleIcon}
                                label="Leads"
                                value="142"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <WebsiteStat
                                icon={TrendingUpIcon}
                                label="Conv"
                                value="1.7%"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    )
}
